import { getBaseUrls, API_TIMEOUT } from './apiConfig';
import { authService } from '../../features/auth/services/authService';

class ApiService {
    private baseUrls = getBaseUrls();
    private token: string | null = null;
    private empresa: string = 'dev';

    constructor() {
        const savedEmpresa = localStorage.getItem('auth_empresa');
        if (savedEmpresa) {
            this.setCompany(savedEmpresa);
        }
    }

    setCompany(empresa: string) {
        this.empresa = empresa;
        this.baseUrls = getBaseUrls(empresa);
        localStorage.setItem('auth_empresa', empresa);
    }

    getCompany(): string {
        return this.empresa;
    }

    setToken(token: string) {
        this.token = token;
        // Usar localStorage para compartilhar token entre abas do mesmo navegador
        // Cada navegador tem seu próprio localStorage, então sessões de navegadores diferentes permanecem independentes
        localStorage.setItem('auth_token', token);
    }

    getToken(): string | null {
        // Sempre buscar o token mais recente do localStorage para garantir sincronização
        const tokenFromStorage = localStorage.getItem('auth_token');
        if (tokenFromStorage) {
            this.token = tokenFromStorage; // Sincronizar token em memória
        }
        return this.token;
    }

    clearToken() {
        this.token = null;
        localStorage.removeItem('auth_token');
    }

    private async fetchWithFallback(
        endpoint: string,
        options: RequestInit = {},
        customTimeout?: number,
        retryWithTokenRenewal: boolean = true
    ): Promise<Response> {
        const headers: Record<string, string> = {
            ...(options.headers as Record<string, string>),
        };

        // Adicionar Content-Type apenas se houver body e não for FormData
        if (options.body && !(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        // CRÍTICO: Usar getValidAccessToken() para garantir token válido antes de cada requisição
        // Isso garante que:
        // 1. O token seja renovado automaticamente se estiver expirando
        // 2. Sempre use o token mais recente e válido
        // 3. Funcione mesmo após quedas de conexão (renova via HTTP)
        // Buffer de 30 minutos para detectar tokens próximos de expirar (token tem 72h de vida)
        const validToken = await authService.getValidAccessToken(30);
        if (validToken) {
            this.token = validToken; // Sincronizar token em memória
            headers['Authorization'] = `Bearer ${this.token}`;
        } else if (this.token) {
            // Fallback: se não conseguiu renovar mas tem token em memória, usar o da memória
            headers['Authorization'] = `Bearer ${this.token}`;
        }

        const fetchOptions: RequestInit = {
            ...options,
            headers,
        };

        // Verificar se há servidores configurados
        if (this.baseUrls.length === 0) {
            throw new Error(
                'Nenhum servidor de API configurado. ' +
                'Crie um arquivo .env na raiz do projeto com REACT_APP_API_SERVER_1 e REACT_APP_API_SERVER_2'
            );
        }

        // Tentar cada servidor
        const timeout = customTimeout || API_TIMEOUT;

        for (const baseUrl of this.baseUrls) {
            const fullUrl = `${baseUrl}${endpoint}`;

            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), timeout);

                const response = await fetch(
                    fullUrl,
                    { 
                        ...fetchOptions, 
                        signal: controller.signal,
                        // CRÍTICO: Passar cache do fetchOptions para garantir que seja respeitado
                        cache: fetchOptions.cache || 'default'
                    }
                );

                clearTimeout(timeoutId);

                if (response.ok) {
                    return response;
                }

                // CRÍTICO: Se receber 400 ou 401, tentar renovar token e refazer requisição
                // Isso resolve o problema de token expirado que não foi detectado preventivamente
                if ((response.status === 400 || response.status === 401) && retryWithTokenRenewal) {
                    const errorData = await response.json().catch(() => ({}));
                    const errorMessage = errorData.message || errorData.errors?.join(', ') || `Erro ${response.status}`;
                    
                    // Verificar se o erro pode ser relacionado a token inválido/expirado
                    const isTokenError = errorMessage.includes('token') || 
                                        errorMessage.includes('Token') ||
                                        errorMessage.includes('autorização') ||
                                        errorMessage.includes('não autorizado') ||
                                        response.status === 401;
                    
                    if (isTokenError) {
                        try {
                            const currentToken = this.token || authService.getToken();
                            // Só renovar via endpoint de TV com JWT de TV — nunca com token de login (corrompe a sessão).
                            if (currentToken && authService.isTelevisaoToken(currentToken)) {
                                const novoToken = await authService.refreshToken(currentToken);
                                if (novoToken) {
                                    this.token = novoToken;
                                    return this.fetchWithFallback(endpoint, options, customTimeout, false);
                                }
                                const { notifyTotemSessionExpired } = await import('./totemSessionRecovery');
                                notifyTotemSessionExpired('401-sem-novo-token');
                            }
                        } catch (refreshError: any) {
                            console.error('[ApiService] ❌ Erro ao renovar token após erro 400/401:', refreshError);
                            const { notifyTotemSessionExpired } = await import('./totemSessionRecovery');
                            notifyTotemSessionExpired('401-refresh-falhou');
                        }
                    }
                }

                if (response.status === 401) {
                    // Não limpar o token automaticamente em 401
                    // Isso permite que múltiplos dispositivos usem o mesmo usuário simultaneamente
                    // O token só será limpo explicitamente no logout ou quando necessário
                    throw new Error('Não autorizado');
                }

                // Erros 4xx (exceto 401) não devem tentar outro servidor
                if (response.status >= 400 && response.status < 500) {
                    const errorData = await response.json().catch(() => ({}));

                    let errorMessage = errorData.message || errorData.errors?.join(', ') || `Erro ${response.status}`;

                    // Incluir detalhes se existirem
                    if (errorData.detalhes) {
                        const detalhesStr = Object.entries(errorData.detalhes)
                            .map(([key, value]) => `${key}: ${value}`)
                            .join('; ');
                        if (detalhesStr) {
                            errorMessage += ` (${detalhesStr})`;
                        }
                    }

                    throw new Error(errorMessage);
                }

            } catch (error: any) {
                console.error(`❌ Erro ao conectar em ${fullUrl}:`, error.message || error);

                // Tratar erros de abort (AbortController)
                if (error.name === 'AbortError' || error.message?.includes('aborted')) {
                    console.warn(`⏱️ Timeout ao conectar em ${fullUrl}, tentando próximo servidor...`);
                    // Se foi abortado por timeout ou outro motivo, tentar próximo servidor
                    continue;
                }

                // Se for erro de cliente (4xx), não tenta o próximo servidor
                if (error.message && error.message !== 'Failed to fetch' && !error.message.includes('network')) {
                    throw error;
                }

                // Se for erro de rede, continua para o próximo servidor
                if (error.message === 'Failed to fetch' || error.message?.includes('network')) {
                    console.warn(`🌐 Erro de rede ao conectar em ${fullUrl}, tentando próximo servidor...`);
                    continue;
                }
            }
        }

        const errorMsg = `❌ Todos os servidores falharam. URLs tentadas: ${this.baseUrls.map(url => `${url}${endpoint}`).join(', ')}`;
        console.error(errorMsg);
        throw new Error(errorMsg);
    }

    async get<T>(endpoint: string, options?: { cache?: RequestCache; headers?: Record<string, string> }): Promise<T> {
        // Se endpoint já tem query params, adicionar cache control
        // Se não tem, adicionar como primeiro param
        const separator = endpoint.includes('?') ? '&' : '?';
        // Usar timestamp + número aleatório para quebrar cache de forma mais agressiva
        // (não '_nocache' que não existe no backend)
        const cacheBust = options?.cache === 'no-store' 
          ? `${separator}t=${Date.now()}-${Math.random().toString(36).substring(7)}` 
          : '';
        const finalEndpoint = cacheBust ? `${endpoint}${cacheBust}` : endpoint;
        
        const fetchOptions: RequestInit = {
            ...options,
            cache: options?.cache || 'default',
            // Adicionar headers de cache-control para garantir dados frescos
            headers: {
                ...(options?.headers || {}),
                ...(options?.cache === 'no-store' ? {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0'
                } : {})
            }
        };
        
        const response = await this.fetchWithFallback(finalEndpoint, fetchOptions);
        const text = await response.text();
        if (!text || text.trim() === '') {
            return [] as any; // Se vazio, retorna array vazio como padrão para GET (geralmente listas)
        }
        try {
            return JSON.parse(text);
        } catch (e) {
            console.warn(`Resposta não é JSON válido em ${finalEndpoint}:`, text);
            throw new Error('Resposta inválida do servidor');
        }
    }

    async post<T>(endpoint: string, data: any, options?: { headers?: Record<string, string> }): Promise<T> {
        const response = await this.fetchWithFallback(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
            headers: options?.headers || {}
        });
        const text = await response.text();
        if (!text || text.trim() === '') {
            return {} as any;
        }
        try {
            return JSON.parse(text);
        } catch (e) {
            // Alguns endpoints retornam booleano como texto (ex: "true")
            if (text.trim() === 'true') return true as any;
            if (text.trim() === 'false') return false as any;

            console.warn(`Resposta não é JSON válido em ${endpoint}:`, text);
            throw new Error('Resposta inválida do servidor');
        }
    }

    /**
     * PUT com corpo multipart (ex.: campo `Foto`). Não define Content-Type manualmente — o browser envia o boundary.
     */
    async putFormData<T>(endpoint: string, formData: FormData): Promise<T> {
        const putTimeout = API_TIMEOUT * 1.5;
        const response = await this.fetchWithFallback(endpoint, {
            method: 'PUT',
            body: formData,
        }, putTimeout);
        const text = await response.text();
        if (!text || text.trim() === '') {
            return {} as T;
        }
        try {
            return JSON.parse(text);
        } catch (e) {
            if (text.trim() === 'true') return true as T;
            if (text.trim() === 'false') return false as T;
            console.warn(`Resposta não é JSON válido em ${endpoint}:`, text);
            throw new Error('Resposta inválida do servidor');
        }
    }

    async put<T>(endpoint: string, data: any): Promise<T> {
        // IMPORTANTE: Remover campos undefined/null antes de serializar
        // JSON.stringify não remove undefined, então precisamos fazer isso manualmente
        const cleanedData = this.removeUndefinedFields(data);

        // Timeout maior para operações PUT (podem ser mais pesadas com muitos dados)
        const putTimeout = API_TIMEOUT * 1.5; // 50% mais tempo para PUT

        const response = await this.fetchWithFallback(endpoint, {
            method: 'PUT',
            body: JSON.stringify(cleanedData),
        }, putTimeout);
        const text = await response.text();
        if (!text || text.trim() === '') {
            return {} as any;
        }
        try {
            return JSON.parse(text);
        } catch (e) {
            // Alguns endpoints retornam booleano como texto (ex: "true")
            if (text.trim() === 'true') return true as any;
            if (text.trim() === 'false') return false as any;

            console.warn(`Resposta não é JSON válido em ${endpoint}:`, text);
            throw new Error('Resposta inválida do servidor');
        }
    }

    /**
     * Remove campos undefined e null de um objeto recursivamente
     * Isso garante que apenas campos com valores sejam enviados no JSON
     * 
     * OTIMIZAÇÃO: Strings (especialmente JSON strings grandes com imagens base64) 
     * não são processadas recursivamente - são mantidas como estão para performance
     */
    private removeUndefinedFields(obj: any): any {
        if (obj === null || obj === undefined) {
            return obj;
        }

        // IMPORTANTE: Strings não precisam ser processadas - manter como estão
        // Isso evita processamento desnecessário de JSON strings grandes (com imagens base64)
        if (typeof obj === 'string') {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj.map(item => this.removeUndefinedFields(item));
        }

        if (typeof obj === 'object') {
            const cleaned: any = {};
            for (const key in obj) {
                if (obj.hasOwnProperty(key)) {
                    const value = obj[key];
                    // Incluir apenas se não for undefined
                    // null pode ser incluído se for intencional
                    if (value !== undefined) {
                        // IMPORTANTE: Se o valor for string (como configuracoes JSON), manter como está
                        // Não processar recursivamente strings para evitar lentidão com base64
                        if (typeof value === 'string') {
                            cleaned[key] = value;
                        } else {
                            cleaned[key] = this.removeUndefinedFields(value);
                        }
                    }
                }
            }
            return cleaned;
        }

        return obj;
    }

    async delete<T>(endpoint: string): Promise<T> {
        const response = await this.fetchWithFallback(endpoint, {
            method: 'DELETE',
        });
        const text = await response.text();
        if (!text || text.trim() === '') {
            return {} as any;
        }
        try {
            return JSON.parse(text);
        } catch (e) {
            // Alguns endpoints retornam booleano como texto (ex: "true")
            if (text.trim() === 'true') return true as any;
            if (text.trim() === 'false') return false as any;

            console.warn(`Resposta não é JSON válido em ${endpoint}:`, text);
            throw new Error('Resposta inválida do servidor');
        }
    }

    async uploadFile(endpoint: string, file: File, fieldName = 'Video'): Promise<any> {
        const formData = new FormData();

        // Garantir que arquivos de vídeo tenham o Content-Type correto
        // Aceita: video/mp4, video/mpeg4 ou extensão .mp4
        let fileToUpload = file;
        const isMp4 = file.name.toLowerCase().endsWith('.mp4');
        const isValidVideoType = file.type === 'video/mp4' || file.type === 'video/mpeg4';

        if (isMp4 && !isValidVideoType) {
            // Se for .mp4 mas não tiver tipo definido ou tipo incorreto, definir como video/mp4
            fileToUpload = new File([file], file.name, { type: 'video/mp4' });
        }

        formData.append(fieldName, fileToUpload);


        const response = await this.fetchWithFallback(endpoint, {
            method: 'POST',
            body: formData,
        });

        // Verificar se a resposta está OK
        if (!response.ok) {
            let errorMessage = `Erro ao fazer upload: ${response.status}`;
            try {
                const errorText = await response.text();
                if (errorText) {
                    try {
                        const errorJson = JSON.parse(errorText);
                        // A resposta pode ser { success: false, message: "..." }
                        errorMessage = errorJson.message || errorJson.error || errorText;
                    } catch {
                        errorMessage = errorText;
                    }
                }
            } catch {
                errorMessage = `Erro HTTP ${response.status}`;
            }
            throw new Error(errorMessage);
        }

        // A resposta retorna bool (true = sucesso, false = erro) ou JSON de erro
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            const text = await response.text();
            if (text && text.trim()) {
                try {
                    const result = JSON.parse(text);
                    // Se for bool, retornar diretamente
                    if (typeof result === 'boolean') {
                        if (!result) {
                            throw new Error('Upload falhou');
                        }
                        return result;
                    }
                    // Se for objeto de erro, lançar exceção
                    if (result.message || result.error) {
                        throw new Error(result.message || result.error || 'Upload falhou');
                    }
                    return result;
                } catch (error) {
                    // Se for erro de parse ou erro lançado acima, propagar
                    if (error instanceof Error) {
                        throw error;
                    }
                    throw new Error('Erro ao processar resposta do servidor');
                }
            }
        }

        // Se não for JSON, verificar se status é OK (considerar sucesso)
        return true;
    }

    async downloadFile(endpoint: string): Promise<Blob> {
        const response = await this.fetchWithFallback(endpoint);
        return response.blob();
    }
}

export const apiService = new ApiService();
